import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Plus,
  Receipt as ReceiptIcon,
  TrendingUp,
  Calendar,
  Code2,
  LogOut,
  ScrollText,
  Camera,
  BookOpen,
  Search,
  Briefcase,
  History,
  Wallet,
  Rocket,
  Briefcase as BriefcaseIcon2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { BillyLogo } from "@/components/BillyLogo";
import CommandPalette from "@/components/CommandPalette";

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT" && document.activeElement?.tagName !== "TEXTAREA") {
        // Only trigger / when not typing in a field
        if (!paletteOpen) {
          e.preventDefault();
          setPaletteOpen(true);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [paletteOpen]);

  const p = location.pathname;
  const onNewBill = p === "/new";
  const onBills = p === "/" || p.startsWith("/bills") || onNewBill;
  const onIncome = p.startsWith("/income");
  const onReceipts = p.startsWith("/receipts");
  const onNewReceipt = p === "/receipts/new";

  const tabs = [
    { to: "/", label: "Bills", testid: "tab-bills", icon: ReceiptIcon, match: (p) => p === "/" || p.startsWith("/bills") || p === "/new" },
    { to: "/receipts", label: "Receipts", testid: "tab-receipts", icon: ScrollText, match: (p) => p.startsWith("/receipts") },
    { to: "/income", label: "Income", testid: "tab-income", icon: TrendingUp, match: (p) => p.startsWith("/income") },
    { to: "/overheads", label: "Overheads", testid: "tab-overheads", icon: BriefcaseIcon2, match: (p) => p.startsWith("/overheads") },
    { to: "/weekly", label: "Weekly", testid: "tab-weekly", icon: Calendar, match: (p) => p.startsWith("/weekly") },
    { to: "/allocate-weekly", label: "Allocate", testid: "tab-allocate", icon: Wallet, match: (p) => p.startsWith("/allocate-weekly") },
    { to: "/grow", label: "Grow", testid: "tab-grow", icon: Rocket, match: (p) => p.startsWith("/grow") },
    { to: "/calendar", label: "Calendar", testid: "tab-calendar", icon: Calendar, match: (p) => p.startsWith("/calendar") },
    { to: "/jobs", label: "Jobs", testid: "tab-jobs", icon: Briefcase, match: (p) => p.startsWith("/jobs") },
    { to: "/pnl", label: "P&L", testid: "tab-pnl", icon: TrendingUp, match: (p) => p.startsWith("/pnl") },
    { to: "/history", label: "History", testid: "tab-history", icon: History, match: (p) => p.startsWith("/history") },
    { to: "/embed", label: "Embed", testid: "tab-embed", icon: Code2, match: (p) => p.startsWith("/embed") },
    { to: "/guide", label: "Guide", testid: "tab-guide", icon: BookOpen, match: (p) => p.startsWith("/guide") },
  ];

  let primaryCta = null;
  if (onBills && !onNewBill) {
    primaryCta = {
      label: "New Bill",
      onClick: () => navigate("/new"),
      testid: "new-bill-btn",
    };
  } else if (onReceipts && !onNewReceipt) {
    primaryCta = {
      label: "New Receipt",
      onClick: () => navigate("/receipts/new"),
      testid: "new-receipt-btn",
    };
  } else if (onIncome) {
    primaryCta = {
      label: "Add Income",
      onClick: () => {
        // Trigger the Income page's add dialog via query param
        navigate("/income?new=1");
      },
      testid: "new-income-btn",
    };
  }

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header
        data-testid="app-header"
        className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-neutral-200"
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="nav-home"
            className="group transition-transform hover:-translate-y-0.5"
          >
            <BillyLogo size={40} tone="dark" />
          </Link>

          <div className="flex items-center gap-2">
            <button
              data-testid="palette-open"
              onClick={() => setPaletteOpen(true)}
              title="Search (⌘K)"
              className="hidden md:inline-flex items-center gap-2 font-bold uppercase tracking-widest text-[10px] border-2 border-black bg-white text-black px-3 py-3 hover:bg-black hover:text-white transition-colors"
            >
              <Search className="h-3.5 w-3.5" strokeWidth={2.5} />
              <span>Search</span>
              <kbd className="ml-1 text-[9px] border border-current px-1 py-0.5 leading-none">⌘K</kbd>
            </button>
            <button
              data-testid="snap-cta"
              onClick={() => navigate("/snap")}
              title="Snap a bill or receipt"
              className="hidden sm:inline-flex items-center gap-2 font-bold uppercase tracking-widest text-xs border-2 border-black bg-black text-white px-5 py-3 hover:bg-white hover:text-black transition-colors"
            >
              <Camera className="h-4 w-4" strokeWidth={2.5} />
              Snap
            </button>
            {primaryCta && (
              <button
                data-testid={primaryCta.testid}
                onClick={primaryCta.onClick}
                className="hidden md:inline-flex items-center gap-2 font-bold uppercase tracking-widest text-xs border-2 border-black bg-white text-black px-4 py-3 hover:bg-black hover:text-white transition-colors"
              >
                <Plus className="h-4 w-4" strokeWidth={3} />
                {primaryCta.label}
              </button>
            )}
            {user && (
              <button
                data-testid="logout-btn"
                onClick={logout}
                title={`Sign out (${user.email})`}
                className="inline-flex items-center gap-2 font-bold uppercase tracking-widest text-[10px] border-2 border-black bg-white text-black px-3 py-3 hover:bg-black hover:text-white transition-colors"
              >
                <LogOut className="h-3.5 w-3.5" strokeWidth={3} />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            )}
          </div>
        </div>

        <nav className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="flex gap-1 overflow-x-auto -mb-px scrollbar-none">
            {tabs.map((t) => {
              const active = t.match(p);
              return (
                <NavLink
                  key={t.to}
                  to={t.to}
                  data-testid={t.testid}
                  className={`relative inline-flex items-center gap-2 px-4 py-3 uppercase tracking-[0.2em] text-[11px] font-bold border-b-2 transition-colors whitespace-nowrap ${
                    active
                      ? "text-black border-black"
                      : "text-neutral-500 border-transparent hover:text-black"
                  }`}
                >
                  <t.icon className="h-3.5 w-3.5" />
                  {t.label}
                </NavLink>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-8 pt-8 pb-28">
        <Outlet />
      </main>

      {/* Mobile FAB — always opens Smart Capture */}
      <button
        data-testid="fab-snap"
        aria-label="Snap a bill or receipt"
        onClick={() => navigate("/snap")}
        className="fab sm:hidden"
      >
        <Camera className="h-7 w-7" strokeWidth={3} />
      </button>

      <footer className="py-8 border-t border-neutral-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8 flex flex-wrap items-center justify-between gap-3 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
          <span>© {new Date().getFullYear()} Billy — The Outdoor Blind Company</span>
          <Link to="/embed" className="hover:text-black transition-colors">
            Get embed code →
          </Link>
        </div>
      </footer>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
};

export default Layout;
